import React, { useEffect, useState } from 'react';

import { startCase } from 'lodash';

import Banner from 'shared/banner';
import Button from 'shared/button';
import LoadingSpinner from 'components/loading_spinner';
import ManagedInput from 'components/form/managed_input';
import Modal from 'components/modal';
import { requiredFields, shouldDisplayRow } from './helpers';
import { getMerge } from '../api';
import { propertyAttributes } from 'bundles/research/index/research_constants';

interface Props {
  deal: Record<string, any>;
  project: Record<string, any>;
  saving?: boolean;
  onClose: () => void;
  onMerge: (properties: Record<string, any>[], attributesMap: Record<string, number | null>) => void;
}

// TODO: Most of this UI is copied over from bundles/research/index/merge_properties.js
// We should refactor the merge action from the research properties index to use a modal instead of
// a separate page, and then we can refactor/reuse this component between both places.

export default function MergePropertiesModal({ deal, project, saving, onClose, onMerge }: Props) {
  async function fetchProperties() {
    const { properties, propertyDefaults } = await getMerge([deal.researchProperty, project.edgeResearchProperty]);
    setProperties(properties);
    setPropertyDefaults(propertyDefaults);
  }

  useEffect(() => {
    if (!properties.length) {
      fetchProperties();
    }
  }, []);

  const [properties, setProperties] = useState<Record<string, any>[]>([]);
  const [propertyDefaults, setPropertyDefaults] = useState<Record<string, any>>({});

  const [attributesMap, setAttributesMap] = useState<Record<string, number | null>>(
    propertyAttributes.reduce(
      (acc, key) => {
        acc[key] = null;
        return acc;
      },
      {}
    )
  );

  const rows: React.ReactNode[] = [];
  const hiddenKeys: string[] = [];
  for (const key of propertyAttributes) {
    if (!shouldDisplayRow(properties, propertyDefaults, key)) {
      hiddenKeys.push(key);
      continue;
    }

    const property = properties.find(property => property.id === attributesMap[key]);

    rows.push(
      <tr key={key}>
        <td>
          <label className="form-label">{startCase(key)}</label>
          {requiredFields.includes(key) && <span className="text-danger">*</span>}
        </td>
        {properties.map(property => (
          <td key={property.id + key}>
            <ManagedInput
              checked={attributesMap[key] === property.id}
              key={key}
              label={Array.isArray(property[key]) ? property[key].join(', ') : property[key]}
              type="checkbox"
              onChange={() => setAttributesMap(prev => ({ ...prev, [key]: property.id }))}
            />
          </td>
        ))}
        <td>{property ? property[key] : ''}</td>
      </tr>
    );
  }

  function selectedAll(id: string) {
    const selected = Object.keys(attributesMap).every((key) => {
      return hiddenKeys.includes(key) || attributesMap[key] === parseInt(id);
    });
    console.log(attributesMap);
    console.log(id);
    console.log(typeof id);
    console.log(selected);
    return selected;
  }

  function onSelectAll(id: number) {
    const newAttributesMap = {};

    Object.keys(attributesMap).map((key) => {
      newAttributesMap[key] = id;
    });

    setAttributesMap(newAttributesMap);
  }

  const propertyHeaders: React.ReactNode[] = [];
  properties.map((property, index) => {
    propertyHeaders.push(
      <th key={index}>
        <ManagedInput
          checked={selectedAll(property.id)}
          label={`Property ${index + 1}`}
          type="checkbox"
          onChange={() => onSelectAll(property.id)}
        />
      </th>
    );
  });

  const content = <>
    <Banner
      content={
        <div className="d-flex flex-column gap-3">
          <div>
            In the table below, you will see all conflicting information between the properties you
            have selected to merge. For each row, select which data you would like to keep.
          </div>
          <div>
            Note that all associations to contacts, comps, activities, etc. will be preserved.
          </div>
        </div>
      }
      variant="info"
    />

    <div className="table-container">
      <table className="table">
        <thead>
          <tr>
            <th>Fields</th>
            {propertyHeaders}
            <th>Merged Property</th>
          </tr>
        </thead>
        <tbody>
          {rows}
        </tbody>
      </table>
    </div>
  </>;

  return <>
    <Modal.Body className="d-flex flex-column gap-3">
      {properties.length ? content : <LoadingSpinner/>}
    </Modal.Body>
    <Modal.Footer>
      <Button.Cancel onClick={onClose} />
      <Button.Primary
        disabled={!properties.length || saving || requiredFields.some(field => !attributesMap[field])}
        label="Merge"
        onClick={() => onMerge(properties, attributesMap)}
      />
    </Modal.Footer>
  </>;
}
